import { IAppointment, IAvailability, IBranch, MAIN_BRANCH_ID } from '@torim-app/types';

export interface IMainBranchSource {
  title?: string;
  /** Explicit name for the main branch, independent of the business name. */
  mainBranchName?: string;
  location?: string;
  phone?: string;
  locationImage?: string;
}

/**
 * Build the synthetic "main branch" entry that represents the business's own
 * primary location. The main branch is implicit (never stored in `branches[]`)
 * and identified by {@link MAIN_BRANCH_ID}. Pass `suffix` (e.g. "(ראשי)") to
 * disambiguate it in admin selectors.
 *
 * The name comes from `mainBranchName` when the owner set one, so a business
 * called "אליאור ביוטי" can name its first location "תל אביב" without the
 * business itself being renamed. Businesses that never touched it keep the
 * legacy behaviour of showing the business title.
 */
export function buildMainBranch(source: IMainBranchSource, suffix?: string): IBranch {
  const resolvedName = source.mainBranchName?.trim() || source.title;
  const baseName = resolvedName || 'סניף ראשי';
  return {
    id: MAIN_BRANCH_ID,
    // The suffix only disambiguates a real name — appending it to the generic
    // fallback would read as "סניף ראשי (ראשי)".
    name: suffix && resolvedName ? `${baseName} ${suffix}` : baseName,
    address: source.location,
    phone: source.phone,
    locationImage: source.locationImage,
  };
}

/** The main branch followed by the additional (stored) branches. */
export function withMainBranch(source: IMainBranchSource, branches: IBranch[] = [], suffix?: string): IBranch[] {
  return [buildMainBranch(source, suffix), ...branches];
}

/**
 * The distinct branch ids a provider works at, read off their weekly working-hours
 * blocks. An untagged block counts as the main branch (same rule as the scheduler).
 */
export function providerBranchIds(availability?: IAvailability): string[] {
  if (!availability) return [];
  const ids = new Set<string>();
  for (const dayBlocks of Object.values(availability)) {
    for (const block of dayBlocks ?? []) {
      ids.add(block?.branchId || MAIN_BRANCH_ID);
    }
  }
  return [...ids];
}

type BranchAwareBusiness = IMainBranchSource & {
  branches?: IBranch[];
  availability?: unknown;
};

/** True when the business actually operates from more than one location. */
export function hasMultipleBranches(business: BranchAwareBusiness): boolean {
  return (business.branches ?? []).some(b => b.active !== false);
}

/**
 * Resolve the branch an appointment belongs to.
 *
 * Appointments booked through the customer site carry an explicit `branchId`.
 * Admin-created ones may not, so fall back to the provider's own working hours:
 * when they work at exactly one branch there is no ambiguity. Returns undefined
 * when the branch genuinely can't be determined, so callers can stay silent
 * rather than naming the wrong location.
 */
export function resolveAppointmentBranch(
  business: BranchAwareBusiness,
  appointment: Pick<IAppointment, 'branchId' | 'providingClient'>
): IBranch | undefined {
  const all = withMainBranch(business, (business.branches ?? []).filter(b => b.active !== false));

  if (appointment.branchId) {
    return all.find(b => b.id === appointment.branchId);
  }

  const availability = (business.availability ?? {}) as Record<string, IAvailability>;
  const ids = providerBranchIds(availability?.[appointment.providingClient]);
  if (ids.length !== 1) return undefined;
  return all.find(b => b.id === ids[0]);
}

/**
 * How the business should be named to a customer for a given appointment: the
 * business name on its own for a single-location business, and "עסק - סניף" once
 * there is more than one location, so a reminder never sends someone to the
 * wrong branch.
 */
export function businessNameForAppointment(
  business: BranchAwareBusiness,
  appointment?: Pick<IAppointment, 'branchId' | 'providingClient'>
): string {
  const title = business.title ?? '';
  if (!appointment || !hasMultipleBranches(business)) return title;

  const branch = resolveAppointmentBranch(business, appointment);
  if (!branch || !branch.name || branch.name === title) return title;
  return `${title} - ${branch.name}`;
}
